import { z } from 'zod';
import { PerformanceBandSchema, ShiftSchema } from './enums';
import { AiInsightSnapshotSchema } from './ai';
import { IdSchema } from './validation';

export const DashboardQuerySchema = z.object({
  academicYearId: IdSchema.optional(),
  termId: IdSchema.optional(),
  classId: IdSchema.optional(),
  teacherId: IdSchema.optional(),
});
export type DashboardQuery = z.infer<typeof DashboardQuerySchema>;

export const StatDeltaSchema = z.object({
  value: z.number(),
  /** Variação no mês corrente (null quando não há base de comparação). */
  deltaThisMonth: z.number().nullable(),
});

export const DashboardAlertSchema = z.object({
  id: z.string(),
  status: z.enum(['information', 'warning', 'error', 'success']),
  title: z.string(),
  body: z.string(),
});
export type DashboardAlert = z.infer<typeof DashboardAlertSchema>;

export const ClassPerformanceSchema = z.object({
  classId: z.string(),
  label: z.string(),
  shift: ShiftSchema,
  averageScore: z.number().nullable(),
  attendanceRate: z.number().nullable(),
  studentCount: z.number().int(),
});
export type ClassPerformance = z.infer<typeof ClassPerformanceSchema>;

export const TopStudentSchema = z.object({
  studentId: z.string(),
  name: z.string(),
  classLabel: z.string(),
  averageScore: z.number(),
  performance: PerformanceBandSchema,
});

export const TeacherMetricSchema = z.object({
  teacherId: z.string(),
  name: z.string(),
  mainSubject: z.string().nullable(),
  mainClass: z.string().nullable(),
  averageScore: z.number().nullable(),
  gradeCount: z.number().int(),
  performance: PerformanceBandSchema,
});

export const PendingOperationSchema = z.object({
  id: z.string(),
  kind: z.enum(['GRADES_PENDING', 'EVENT', 'ATTENDANCE_PENDING']),
  title: z.string(),
  subtitle: z.string(),
  dueLabel: z.enum(['TODAY', 'TOMORROW', 'THIS_WEEK', 'OVERDUE']),
  date: z.string().nullable(),
});
export type PendingOperation = z.infer<typeof PendingOperationSchema>;

export const SummaryMetricsSchema = z.object({
  averageScore: z.number().nullable(),
  averageScoreDeltaPct: z.number().nullable(),
  attendanceRate: z.number().nullable(),
  attendanceDeltaPct: z.number().nullable(),
  dropoutRate: z.number().nullable(),
  dropoutDeltaPct: z.number().nullable(),
});
export type SummaryMetrics = z.infer<typeof SummaryMetricsSchema>;

export const DashboardSchema = z.object({
  period: z.object({
    academicYearId: z.string().nullable(),
    academicYearLabel: z.string().nullable(),
    termId: z.string().nullable(),
    termName: z.string().nullable(),
  }),
  alerts: z.array(DashboardAlertSchema),
  stats: z.object({
    totalClasses: StatDeltaSchema,
    activeStudents: StatDeltaSchema,
    activeTeachers: StatDeltaSchema,
  }),
  summary: SummaryMetricsSchema,
  classPerformance: z.array(ClassPerformanceSchema),
  topStudents: z.array(TopStudentSchema),
  studentsAtRisk: z.number().int(),
  teacherMetrics: z.array(TeacherMetricSchema),
  pendingOperations: z.array(PendingOperationSchema),
  insights: AiInsightSnapshotSchema.nullable(),
  computedAt: z.string(),
});
export type Dashboard = z.infer<typeof DashboardSchema>;
